import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  orderBy,
} from 'firebase/firestore';
import { db } from './firebase';

export interface DashboardStats {
  totalUsers: number;
  totalDonors: number;
  totalDonees: number;
  pendingReviews: number;
  todayRegistrations: number;
  monthRegistrations: number;
}

export interface UserGrowthData {
  date: string;
  donors: number;
  donees: number;
  total: number;
}

/**
 * ダッシュボード統計情報取得
 */
export async function fetchDashboardStats(): Promise<DashboardStats> {
  try {
    const usersRef = collection(db, 'users');

    // 全ユーザー数
    const allUsersSnapshot = await getDocs(query(usersRef));
    const totalUsers = allUsersSnapshot.size;

    // ドナー数
    const donorsSnapshot = await getDocs(query(usersRef, where('role', '==', 'donor')));
    const totalDonors = donorsSnapshot.size;

    // ドニー数
    const doneesSnapshot = await getDocs(query(usersRef, where('role', '==', 'donee')));
    const totalDonees = doneesSnapshot.size;

    // 審査待ち数
    const pendingSnapshot = await getDocs(
      query(usersRef, where('status', '==', 'under_review'))
    );
    const pendingReviews = pendingSnapshot.size;

    // 今日の新規登録
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTimestamp = Timestamp.fromDate(today);

    const todayUsersSnapshot = await getDocs(
      query(usersRef, where('createdAt', '>=', todayTimestamp))
    );
    const todayRegistrations = todayUsersSnapshot.size;

    // 今月の新規登録
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const monthStartTimestamp = Timestamp.fromDate(monthStart);

    const monthUsersSnapshot = await getDocs(
      query(usersRef, where('createdAt', '>=', monthStartTimestamp))
    );
    const monthRegistrations = monthUsersSnapshot.size;

    return {
      totalUsers,
      totalDonors,
      totalDonees,
      pendingReviews,
      todayRegistrations,
      monthRegistrations,
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw new Error('統計情報の取得に失敗しました');
  }
}

/**
 * ユーザー数推移データ取得（過去30日）
 */
export async function fetchUserGrowthData(): Promise<UserGrowthData[]> {
  try {
    const usersRef = collection(db, 'users');
    
    // 30日前の日付
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);
    const thirtyDaysAgoTimestamp = Timestamp.fromDate(thirtyDaysAgo);

    // 過去30日のユーザーを取得
    const usersQuery = query(
      usersRef,
      where('createdAt', '>=', thirtyDaysAgoTimestamp),
      orderBy('createdAt', 'asc')
    );
    const snapshot = await getDocs(usersQuery);

    // 日付ごとに集計
    const dateCounts: Record<string, { donors: number; donees: number }> = {};
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate();
      if (!createdAt) return;

      const dateKey = createdAt.toISOString().split('T')[0];
      
      if (!dateCounts[dateKey]) {
        dateCounts[dateKey] = { donors: 0, donees: 0 };
      }

      if (data.role === 'donor') {
        dateCounts[dateKey].donors++;
      } else if (data.role === 'donee') {
        dateCounts[dateKey].donees++;
      }
    });

    // 過去30日分の配列を生成（データがない日も含む）
    const growthData: UserGrowthData[] = [];
    let cumulativeDonors = 0;
    let cumulativeDonees = 0;

    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      const dateKey = date.toISOString().split('T')[0];

      const counts = dateCounts[dateKey] || { donors: 0, donees: 0 };
      cumulativeDonors += counts.donors;
      cumulativeDonees += counts.donees;

      growthData.push({
        date: new Intl.DateTimeFormat('ja-JP', {
          month: '2-digit',
          day: '2-digit',
        }).format(date),
        donors: cumulativeDonors,
        donees: cumulativeDonees,
        total: cumulativeDonors + cumulativeDonees,
      });
    }

    return growthData;
  } catch (error) {
    console.error('Error fetching user growth data:', error);
    throw new Error('ユーザー数推移データの取得に失敗しました');
  }
}
